import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowRight, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { trendingPostsQueryOptions } from '@/lib/posts';

/**
 * Top articles by page views over the last 7 days — the reader-facing
 * counterpart to the audit "ที่เกิดบ่อย" card. Views come from the
 * pageview-flush cron (post_view_daily), so the list lags real time by at
 * most one flush cycle (~5 min). Each row links to the post editor.
 */
export function TopViewedCard() {
  const q = useQuery(trendingPostsQueryOptions('7d', 8));
  const items = q.data?.items ?? [];
  const max = items[0]?.views ?? 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <Eye className="h-4 w-4 text-slate-400" />
          อ่านมากสุด 7 วัน
        </CardTitle>
        <Link
          to="/posts"
          className="flex items-center text-xs text-blue-600 hover:underline dark:text-blue-400"
        >
          ดูโพสต์ <ArrowRight className="ml-0.5 h-3 w-3" />
        </Link>
      </CardHeader>
      <CardContent>
        {q.isLoading ? (
          <p className="text-sm text-slate-500">กำลังโหลด...</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-400">ยังไม่มียอดวิวในช่วงนี้</p>
        ) : (
          <ul className="space-y-1.5">
            {items.map((it) => {
              const pct = max > 0 ? Math.max(6, Math.round((it.views / max) * 100)) : 0;
              return (
                <li key={it.id}>
                  <Link
                    to="/posts/$postId"
                    params={{ postId: it.id }}
                    className="group flex items-center gap-2 text-xs"
                    title={it.title}
                  >
                    <span className="w-40 shrink-0 truncate text-slate-700 group-hover:text-slate-900 dark:text-slate-300 dark:group-hover:text-slate-100">
                      {it.title}
                    </span>
                    <div className="relative h-3 flex-1 overflow-hidden rounded bg-slate-100 dark:bg-slate-800">
                      <div
                        className="absolute inset-y-0 left-0 bg-blue-400 group-hover:bg-blue-500 dark:bg-blue-600 dark:group-hover:bg-blue-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="w-12 shrink-0 text-right tabular-nums text-slate-500">
                      {it.views.toLocaleString('th-TH')}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
